import React, { useState, forwardRef, useImperativeHandle, useMemo } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Paper,
  Button,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { canPerformAction, ActionKey } from '../../../auth/actionMatrix';
import AnimatedPage from '../../../components/AnimatedPage';
import { canAccessPage, PageKey } from '../../../auth/accessMatrix';

interface TradersLayoutRef {
  handleOpenDialog: () => void;
}

const TradersLayout = forwardRef<TradersLayoutRef>((props, ref) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const canViewTraders = canAccessPage(currentUser, PageKey.TRADERS);
  const canManageTraders = canAccessPage(currentUser, PageKey.TRADER_OPERATIONS);
  const canAccessSupport = canAccessPage(currentUser, PageKey.TRADER_SUPPORT);
  const [value, setValue] = useState(() => {
    const path = location.pathname;
    if (path.includes('/rates')) return 'rates';
    if (path.includes('/balances')) return 'balances';
    if (path.includes('/registry')) return 'registry';
    if (path.includes('/support')) return 'support';
    if (path.includes('/statistics')) return 'statistics';
    if (path.includes('/registrations')) return 'registrations';
    if (path.includes('/groups')) return 'groups';
    return 'list';
  });

  const canCreateTrader = useMemo(() => canPerformAction(currentUser, ActionKey.TRADERS_CREATE), [currentUser]);
  const canCreateBalance = useMemo(() => canPerformAction(currentUser, ActionKey.TRADER_BALANCES_CREATE), [currentUser]);
  const canCreateRate = useMemo(() => canPerformAction(currentUser, ActionKey.TRADER_RATES_CREATE), [currentUser]);

  useImperativeHandle(ref, () => ({
    handleOpenDialog: () => {
      const path = location.pathname;
      if (path.includes('/rates')) {
        // @ts-ignore
        window.traderRatesRef?.handleOpenDialog();
      } else if (path.includes('/balances')) {
        // @ts-ignore
        window.traderBalancesRef?.handleOpenDialog();
      } else if (path.includes('/groups')) {
        // @ts-ignore
        window.traderGroupsRef?.handleOpenDialog();
      } else {
        // @ts-ignore
        window.tradersListRef?.handleOpenDialog();
      }
    }
  }));

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
    if (newValue === 'list') {
      navigate('/traders');
    } else {
      navigate(`/traders/${newValue}`);
    }
  };

  const getButtonText = () => {
    switch (value) {
      case 'rates':
        return 'Добавить ставку';
      case 'balances':
        return 'Добавить баланс';
      case 'groups':
        return 'Добавить группу';
      default:
        return 'Добавить трейдера';
    }
  };

  const isCreateActionTab = value === 'list' || value === 'balances' || value === 'rates' || value === 'groups';
  const hasCreatePermission = (value === 'list' && canCreateTrader) || (value === 'balances' && canCreateBalance) || (value === 'rates' && canCreateRate) || (value === 'groups' && canCreateTrader);
  const showRegistryExportButton = value === 'registry';
  const showSupportCreateButton = value === 'support';

  return (
    <AnimatedPage>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Трейдеры
          </Typography>
          {(!isCreateActionTab || !hasCreatePermission) && !showRegistryExportButton && !showSupportCreateButton ? null : (
            <Box display="flex" gap={1}>
              {isCreateActionTab && hasCreatePermission && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    // @ts-ignore
                    window.tradersLayoutRef?.handleOpenDialog();
                  }}
                  disabled={!hasCreatePermission}
                >
                  {getButtonText()}
                </Button>
              )}
              {showSupportCreateButton && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    // @ts-ignore
                    window.traderSupportRef?.handleOpenDialog?.();
                  }}
                >
                  Добавить чат
                </Button>
              )}
              {showRegistryExportButton && (
                <>
                  <Button
                    variant="contained"
                    onClick={() => {
                      // @ts-ignore
                      window.traderRegistryRef?.handleExportXlsx?.();
                    }}
                  >
                    Выгрузить XLSX
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => {
                      // @ts-ignore
                      window.traderRegistryRef?.handleExportCsv?.();
                    }}
                  >
                    Выгрузить CSV
                  </Button>
                </>
              )}
            </Box>
          )}
        </Box>

        <Paper sx={{ mb: 3 }}>
          <Tabs value={value} onChange={handleChange} variant="scrollable" scrollButtons="auto">
            {canViewTraders && <Tab label="Трейдеры" value="list" />}
            {canManageTraders && <Tab label="Балансы" value="balances" />}
            {canManageTraders && <Tab label="Ставки" value="rates" />}
            {canManageTraders && <Tab label="Реестр" value="registry" />}
            {canAccessSupport && <Tab label="Поддержка" value="support" />}
            {canManageTraders && <Tab label="Статистика" value="statistics" />}
            {canManageTraders && <Tab label="Регистрации" value="registrations" />}
            {canManageTraders && <Tab label="Группы" value="groups" />}
          </Tabs>
        </Paper>
        <Outlet />
      </Box>
    </AnimatedPage>
  );
});

export default TradersLayout; 
